import * as dtv from "../../../lib/data/values.js";
import { i18n } from "../../../lib/globalization/i18n.js";
import { FileRepositoryManager } from "./managers/FileRepositoryManager.js";
import { Services } from "../../../lib/services/Services.js";
import { Module } from "../../../lib/ui/Module.js";
import { Button } from "../../../lib/ui/controls/Button.js";
import { ModalDialog } from "../../../lib/ui/controls/dialogs/ModalDialog.js";
import { Text } from "../../../lib/ui/controls/Text.js";
import { TextBox } from "../../../lib/ui/controls/TextBox.js";
import { Tabs } from "../../../lib/ui/controls/Tabs.js";
import { openFileContentInNewWindow } from "./utils/file-content.js";
import {
    FILE_REPOSITORY_PERMISSIONS,
    canManageFileRepositoryItem,
    canModifyFileRepositoryItem,
    canOpenFileRepositoryItem,
    canWriteFileRepositoryItems,
} from "./utils/file-repository-permissions.js";
import { Grid } from "../../../lib/ui/controls/Grid.js";
import { FolderPermissionPanel } from "./components/FolderPermissionPanel.js";

export class FileRepositoryBrowser extends Module {

    static RENDER_THUMNAILS = 'thumbnails';
    static RENDER_DETAILS = 'details';
    static TAB_CONTENT = 'content';
    static TAB_PERMISSIONS = 'permissions';

    static PERMISSIONS = FILE_REPOSITORY_PERMISSIONS;
    constructor(parent, options = {}) {

        options = Object.assign({
            moduleName: 'FileRepositoryBrowser',
            moduleTitle: 'Úložiště souborů',
            renderMode: FileRepositoryBrowser.RENDER_DETAILS,
        }, options || {});

        super(parent, options);

        this.fileRepositoryManager = null;

        this.folderData = null;
        this.folderSubfolders = [];
        this.folderFiles = [];
        this.loadError = null;
        this.isLoading = false;

        this.activeTabKey = FileRepositoryBrowser.TAB_CONTENT;
        this.panelTabs = new Tabs(this, {
            activeTabKey: () => this.activeTabKey,
            onActiveTabKeyChange: (key) => {
                this.activeTabKey = key;
                this.invalidate();
            },
            tabs: [
                { key: FileRepositoryBrowser.TAB_CONTENT, label: 'Obsah složky' },
                { key: FileRepositoryBrowser.TAB_PERMISSIONS, label: 'Oprávnění' },
            ],
        });

        this.folderPermissionPanel = new FolderPermissionPanel(this, {
            getFolderData: () => this.folderData,
            getFileRepositoryManager: () => this.getFileRepositoryManager(),
            onOpenFolder: (folderUid) => this.openFolder(folderUid),
            onReloadRequested: async () => this.reloadCurrentFolder(),
        });

    };

    // --------------------------------------------------------------------------------------------

    getActiveFolderUid() {
        return this.getApp()?.getActiveRoute()?.params?.uid || null;
    };

    getCurrentFolderLabel() {
        return this.folderData?.name || 'Kořenová složka';
    };

    getFolderPath() {

        const folderPath = this.folderData?.folderPath;
        if (Array.isArray(folderPath) && folderPath.length > 0)
            return folderPath;

        if (!this.folderData)
            return [];

        return [{
            uid: this.folderData.uid || null,
            name: this.getCurrentFolderLabel(),
        }];

    };

    getParentFolderUid() {

        const folderPath = this.getFolderPath();
        if (folderPath.length >= 2)
            return folderPath[folderPath.length - 2]?.uid || null;

        return this.folderData?.parentFolderUid || null;

    };

    getActivePanelTab() {
        return this.activeTabKey || FileRepositoryBrowser.TAB_CONTENT;
    };

    getFileRepositoryManager() {

        if (!this.fileRepositoryManager)
            this.fileRepositoryManager = new FileRepositoryManager(this.getApp()?.getAuthManager?.() || null);

        return this.fileRepositoryManager;

    };

    getLoadErrorSubtitle() {

        const error = this.loadError;
        const details = error?.details || error?.error || error || null;
        const detailMessage = details?.description || details?.message || null;

        if (detailMessage && detailMessage !== 'RemoteDataService call error')
            return detailMessage;

        if (error?.message && error.message !== 'RemoteDataService call error')
            return error.message;

        return 'Zkus načtení zopakovat nebo zkontroluj oprávnění.';

    };

    canUserOpenFolder(folder) {
        return canOpenFileRepositoryItem(folder);
    };

    canUserOpenFile(file) {
        return canOpenFileRepositoryItem(file);
    };

    canUserWriteItems(folder = this.folderData) {
        return canWriteFileRepositoryItems(folder);
    };

    canUserManageItems(folder = this.folderData) {
        if (!folder)
            return false;
        return this.canUserWriteItems(folder);
    };

    isRootFolder(folder = this.folderData) {
        return Array.isArray(folder?.folderPath) && folder.folderPath.length === 1;
    };

    canUserUploadFiles(folder = this.folderData) {
        if (!folder)
            return false;
        if (this.isRootFolder(folder))
            return false;
        return this.canUserWriteItems(folder);
    };

    canUserDeleteFolder(folder) {
        if (!folder?.uid)
            return false;
        return canManageFileRepositoryItem(folder);
    };

    canUserEditFolder(folder) {
        if (!folder?.uid)
            return false;
        return canModifyFileRepositoryItem(folder);
    };

    canUserDeleteFile(file) {
        if (!file?.uid)
            return false;
        return canManageFileRepositoryItem(file);
    };

    canUserEditFile(file) {
        if (!file?.uid)
            return false;
        return canWriteFileRepositoryItems(file);
    };

    async reloadCurrentFolder() {

        const app = this.getApp();
        const params = app?.getActiveRoute()?.params || {};

        this.isLoading = true;
        this.loadError = null;
        this.invalidate();
        app?.setBusyFlag?.();

        try {
            await this.refresh(params);
            this.invalidate();
        } catch (error) {
            this.handleError(error);
        } finally {
            app?.clearBusyFlag?.();
        }

    };

    async uploadFiles() {

        const folder = this.folderData || null;
        const folderUid = folder?.uid || null;
        if (!folderUid) {
            this.handleError('Aktuální složka není k dispozici pro nahrání souborů.');
            return;
        }
        if (!this.canUserUploadFiles(folder)) {
            this.handleError('Do kořenové složky nelze nahrávat soubory.');
            return;
        }

        const uploadPanel = this.getApp().getUploadPanel();

        Array.from(arguments[0] || []).forEach((file) => {
            uploadPanel.uploadFile(file, {
                folderUid,
                onSuccess: async () => {
                    await this.reloadCurrentFolder();
                }
            });
        });

    };

    createFolder() {

        const parentFolderUid = this.folderData?.uid || null;
        if (!parentFolderUid) {
            this.handleError('Aktuální složka není k dispozici pro vytvoření podsložky.');
            return;
        }

        const app = this.getApp();
        const browser = this;
        const folderNameInput = new TextBox(null, {
            value: '',
            autofocus: true,
            css: '.responsive'
        });

        const modal = new ModalDialog(null, {
            title: 'Vytvořit složku',
            controls: [
                (
                    new Grid(null, {})
                        .cell({ controls: [new Text(null, { text: 'Zadej název nové složky.' })] })
                        .cell({ controls: [folderNameInput] })
                )
            ],
            buttons: [
                new Button(null, {
                    label: i18n.t('button.cancel.label'),
                    icon: 'cancel',
                    css: '.text',
                    onClick: createCanceled
                }),
                new Button(null, {
                    label: 'Vytvořit',
                    icon: 'confirm',
                    onClick: createConfirmed
                }),
            ]
        });

        modal.show(app);

        function createCanceled() {
            modal.hide();
        };

        async function createConfirmed() {

            const folderName = String(folderNameInput.getValue() || '').trim();
            if (!folderName) {
                browser.handleError('Zadej název nové složky.');
                return;
            }

            modal.hide();
            app.setBusyFlag();

            try {
                await browser.getFileRepositoryManager().createFolder(parentFolderUid, {
                    name: folderName,
                });
                await browser.reloadCurrentFolder();
            } catch (error) {
                app.handleError(error);
            } finally {
                app.clearBusyFlag();
            }

        };

    };

    // --------------------------------------------------------------------------------------------

    async _refresh(options, activeRoute) {

        await super._refresh(options, activeRoute);

        this.isLoading = true;
        this.loadError = null;
        const params = activeRoute?.params || {};

        try {

            let data = [];

            if (params?.uid) {

                data = await Promise.all([
                    this.getFileRepositoryManager().getFolder(params.uid),
                    this.getFileRepositoryManager().getFolderSubfolders(params.uid),
                    this.getFileRepositoryManager().getFolderFiles(params.uid)
                ]);

            } else {

                data = await Promise.all([
                    this.getFileRepositoryManager().getRootFolder(),
                    this.getFileRepositoryManager().getRootFolderSubfolders(),
                    this.getFileRepositoryManager().getRootFolderFiles()
                ]);

            }

            this.folderData = data[0] || null;
            this.folderSubfolders = Array.isArray(data[1]?.data) ? data[1].data : [];
            this.folderFiles = Array.isArray(data[2]?.data) ? data[2].data : [];

            return true;

        } catch (error) {

            this.folderData = null;
            this.folderSubfolders = [];
            this.folderFiles = [];
            this.loadError = error;
            return false;

        } finally {

            this.isLoading = false;

        }

    };

    // --------------------------------------------------------------------------------------------

    openFolder(folderUid) {

        if (!folderUid) {
            this.getApp().routeTo('file-repository');
            return;
        }

        this.getApp().routeTo('file-repository-folder', { uid: folderUid });

    };

    async openFile(fileUid) {

        const fileContentUrl = this.getFileRepositoryManager().getFileContentUrl(fileUid);
        return openFileContentInNewWindow(fileUid, fileContentUrl, this.getApp(), (error) => this.handleError(error));

    };

    deleteFolder(folderUid) {

        const folder = (this.folderSubfolders || []).find((item) => item?.uid === folderUid);
        if (!folder) {
            this.handleError('Složka nebyla nalezena.');
            return;
        }

        const app = this.getApp();
        const browser = this;

        const modal = new ModalDialog(null, {
            title: i18n.t('module.data_browser.title_record_delete'),
            controls: [
                new Text(null, { text: `Odstranit složku "${folder.name}"?` }),
            ],
            buttons: [
                new Button(null, {
                    label: i18n.t('button.delete.label'),
                    icon: 'confirm',
                    onClick: deleteConfirmed
                }),
                new Button(null, {
                    label: i18n.t('button.cancel.label'),
                    icon: 'cancel',
                    css: '.outlined',
                    onClick: deleteCanceled
                }),
            ]
        });

        modal.show(app);

        function deleteCanceled() {
            modal.hide();
        };

        async function deleteConfirmed() {

            modal.hide();
            app.setBusyFlag();

            try {
                await browser.getFileRepositoryManager().deleteFolder(folder.uid);
                await browser.reloadCurrentFolder();
            } catch (error) {
                app.handleError(error);
            } finally {
                app.clearBusyFlag();
            }

        };

    };

    editFolder(folderUid) {

        const folder = (this.folderSubfolders || []).find((item) => item?.uid === folderUid);
        if (!folder) {
            this.handleError('Složka nebyla nalezena.');
            return;
        }

        const app = this.getApp();
        const browser = this;
        const nameInput = new TextBox(null, {
            value: folder?.name || '',
            autofocus: true,
            css: '.responsive'
        });
        const descriptionInput = new TextBox(null, {
            value: folder?.description || '',
            multiline: true,
            css: '.responsive'
        });

        const modal = new ModalDialog(null, {
            title: 'Upravit složku',
            controls: [
                (
                    new Grid(null, {})
                        .cell({ controls: [new Text(null, { text: 'Název složky' })] })
                        .cell({ controls: [nameInput] })
                        .cell({ controls: [new Text(null, { text: 'Popis' })] })
                        .cell({ controls: [descriptionInput] })
                )
            ],
            buttons: [
                new Button(null, {
                    label: i18n.t('button.cancel.label'),
                    icon: 'cancel',
                    css: '.text',
                    onClick: () => modal.hide()
                }),
                new Button(null, {
                    label: 'Uložit',
                    icon: 'confirm',
                    onClick: saveConfirmed
                }),
            ]
        });

        modal.show(app);

        async function saveConfirmed() {

            const name = String(nameInput.getValue() || '').trim();
            if (!name) {
                browser.handleError('Zadej název složky.');
                return;
            }

            modal.hide();
            app.setBusyFlag();

            try {
                await browser.getFileRepositoryManager().updateFolder(folder.uid, {
                    name,
                    description: String(descriptionInput.getValue() || '').trim(),
                });
                await browser.reloadCurrentFolder();
            } catch (error) {
                app.handleError(error);
            } finally {
                app.clearBusyFlag();
            }

        };

    };

    deleteFile(fileUid) {

        const file = (this.folderFiles || []).find((item) => item?.uid === fileUid);
        if (!file) {
            this.handleError('Soubor nebyl nalezen.');
            return;
        }

        const app = this.getApp();
        const browser = this;

        const modal = new ModalDialog(null, {
            title: i18n.t('module.data_browser.title_record_delete'),
            controls: [
                new Text(null, { text: `Odstranit soubor "${file.name}"?` }),
            ],
            buttons: [
                new Button(null, {
                    label: i18n.t('button.delete.label'),
                    icon: 'confirm',
                    onClick: deleteConfirmed
                }),
                new Button(null, {
                    label: i18n.t('button.cancel.label'),
                    icon: 'cancel',
                    css: '.outlined',
                    onClick: deleteCanceled
                }),
            ]
        });

        modal.show(app);

        function deleteCanceled() {
            modal.hide();
        };

        async function deleteConfirmed() {

            modal.hide();
            app.setBusyFlag();

            try {
                await browser.getFileRepositoryManager().deleteFile(file.uid);
                await browser.reloadCurrentFolder();
            } catch (error) {
                app.handleError(error);
            } finally {
                app.clearBusyFlag();
            }

        };

    };

    editFile(fileUid) {

        const file = (this.folderFiles || []).find((item) => item?.uid === fileUid);
        if (!file) {
            this.handleError('Soubor nebyl nalezen.');
            return;
        }

        const app = this.getApp();
        const browser = this;
        const nameInput = new TextBox(null, {
            value: file?.name || '',
            autofocus: true,
            css: '.responsive'
        });
        const descriptionInput = new TextBox(null, {
            value: file?.description || '',
            multiline: true,
            css: '.responsive'
        });
        const notesInput = new TextBox(null, {
            value: file?.notes || '',
            multiline: true,
            css: '.responsive'
        });

        const modal = new ModalDialog(null, {
            title: 'Upravit soubor',
            controls: [
                (
                    new Grid(null, {})
                        .cell({ controls: [new Text(null, { text: 'Název souboru' })] })
                        .cell({ controls: [nameInput] })
                        .cell({ controls: [new Text(null, { text: 'Popis' })] })
                        .cell({ controls: [descriptionInput] })
                        .cell({ controls: [new Text(null, { text: 'Poznámky' })] })
                        .cell({ controls: [notesInput] })
                )
            ],
            buttons: [
                new Button(null, {
                    label: i18n.t('button.cancel.label'),
                    icon: 'cancel',
                    css: '.text',
                    onClick: () => modal.hide()
                }),
                new Button(null, {
                    label: 'Uložit',
                    icon: 'confirm',
                    onClick: saveConfirmed
                }),
            ]
        });

        modal.show(app);

        async function saveConfirmed() {

            const name = String(nameInput.getValue() || '').trim();
            if (!name) {
                browser.handleError('Zadej název souboru.');
                return;
            }

            modal.hide();
            app.setBusyFlag();

            try {
                await browser.getFileRepositoryManager().updateFile(file.uid, {
                    name,
                    description: String(descriptionInput.getValue() || '').trim(),
                    notes: String(notesInput.getValue() || '').trim(),
                });
                await browser.reloadCurrentFolder();
            } catch (error) {
                app.handleError(error);
            } finally {
                app.clearBusyFlag();
            }

        };

    };

    // --------------------------------------------------------------------------------------------

    _render(vm) {

        const ct = Services.get('templates');
        const browser = this;

        return ct.module([
            ct.header(ct.concat([
                renderModuleHeader(),
            ])),
            ct.section(ct.concat([
                renderModuleContent(),
            ])),
        ]);

        function renderModuleHeader() {

            return [
                renderFolderInfo(),
                renderModuleNav(),
                renderFoldersNav(),
            ];

        };

        function renderFolderInfo() {
            const folderPath = browser.getFolderPath();
            const renderedFolderPath = [];

            folderPath.forEach((folder, index) => {

                if (index > 0)
                    renderedFolderPath.push(' / ');

                const isLast = index === folderPath.length - 1;
                const folderUid = folder?.uid || null;
                const folderName = folder?.name || browser.getCurrentFolderLabel();

                renderedFolderPath.push(
                    isLast
                        ? ct.span('.bold', [folderName])
                        : ct.span('.pointer-cursor', {
                            onclick: () => { browser.openFolder(folderUid); }
                        }, [folderName])
                );

            });

            return ct.row('.border.medium-round.medium-padding.wrap', [
                ct.span('.bold', ['Složka:']),
                ' ',
                ...(
                    renderedFolderPath.length > 0
                        ? renderedFolderPath
                        : [ct.span([browser.getCurrentFolderLabel()])]
                ),
            ]);

        };

        function renderModuleNav() {

            return null;

        };

        function renderModuleContent() {

            return [
                browser.panelTabs.render(vm),
                renderPanelTab(),
            ];

        };

        function renderPanelTab() {

            if (browser.getActivePanelTab() === FileRepositoryBrowser.TAB_PERMISSIONS)
                return browser.folderPermissionPanel.render();

            return ct.table('.lined', [
                ct.thead([
                    ct.trow([
                        ct.thcell(['Název']),
                        ct.thcell('.min', ['Typ']),
                        ct.thcell('.min.right-align', ['Velikost']),
                        ct.thcell('.min.right-align', ['Změněno']),
                        ct.thcell('.min.right-align', ['Akce']),
                    ]),
                ]),
                ct.tbody(ct.concat([
                    renderFolders(),
                    renderFiles(),
                ])),
            ]);

        };

        function renderFoldersNav() {

            const canManageItems = browser.canUserManageItems();
            const canUploadFiles = browser.canUserUploadFiles();
            const uploadTitle = browser.isRootFolder()
                ? 'Do kořenové složky nelze nahrávat soubory.'
                : (canUploadFiles ? null : 'Nemáš oprávnění upravovat obsah této složky.');

            return ct.nav([
                ct.iconButton('refresh', '.circle.transparent', {
                    onclick: () => { browser.reloadCurrentFolder(); }
                }),
                ct.navgap(),
                ct.button('Vytvořit složku', 'folder-plus', {
                    disabled: !canManageItems,
                    title: canManageItems ? null : 'Nemáš oprávnění upravovat obsah této složky.',
                    onclick: () => browser.createFolder()
                }),
                ct.uploadButton('Nahrát soubor', 'upload', {
                    disabled: !canUploadFiles,
                    title: uploadTitle,
                    onclick: (e) => browser.uploadFiles(e.files)
                }, {
                    multiple: true,
                }),
            ]);

        };

        function renderFolders() {

            let folders = [];

            const parentFolderUid = browser.getParentFolderUid();

            if (parentFolderUid) {
                folders.push(
                    ct.trow([
                        ct.tcell([
                            ct.span('.bold.pointer-cursor', {
                                onclick: () => { browser.openFolder(parentFolderUid); }
                            }, [
                                ct.icon('arrow-turn-left-up'),
                                ' ',
                                '(nadřazená složka)',
                            ]),
                        ]),
                        ct.tcell('.min', ['Složka']),
                        ct.tcell('.min.right-align', ['-']),
                        ct.tcell('.min.right-align', ['-']),
                        ct.tcell('.min.right-align', ['-']),
                    ])
                );
            }

            (browser.folderSubfolders || []).forEach((folder) => {

                const userCanOpen = browser.canUserOpenFolder(folder);
                const userCanEdit = browser.canUserEditFolder(folder);
                const userCanDelete = browser.canUserDeleteFolder(folder);
                const folderNameTitle = userCanOpen
                    ? null
                    : 'Nemáš oprávnění otevřít tuto složku.';

                folders.push(
                    ct.trow([
                        ct.tcell([
                            (
                                userCanOpen
                                    ? ct.span('.bold.pointer-cursor', { onclick: () => { browser.openFolder(folder.uid); } }, [
                                        ct.icon('folder'),
                                        ' ',
                                        folder.name,
                                    ])
                                    : ct.span('.italic.pointer-not-allowed', { title: folderNameTitle }, [
                                        ct.icon('folder'),
                                        ' ',
                                        folder.name,
                                    ])
                            ),
                        ]),
                        ct.tcell('.min', ['Složka']),
                        ct.tcell('.min.right-align', ['-']),
                        ct.tcell('.min.right-align', [
                            dtv.formatDatetime(folder.changedAt) || '-',
                        ]),
                        ct.tcell('.min.right-align', [
                            (
                                userCanEdit
                                    ? ct.iconButton('edit', '.transparent.circle', {
                                        title: 'Upravit složku',
                                        onclick: () => { browser.editFolder(folder.uid); }
                                    })
                                    : null
                            ),
                            (
                                userCanDelete
                                    ? ct.iconButton('delete', '.transparent.circle', {
                                        title: 'Odstranit složku',
                                        onclick: () => { browser.deleteFolder(folder.uid); }
                                    })
                                    : null
                            ),
                        ]),
                    ])
                );

            });

            return folders;

        };

        function renderFiles() {

            if (browser.isLoading) {
                return [
                    renderStateRow({
                        title: 'Načítám obsah složky',
                        subtitle: 'Počkej chvíli, připravuji aktuální seznam souborů a složek.',
                    }),
                ];
            }

            if (browser.loadError) {
                return [
                    renderStateRow({
                        title: 'Složku se nepodařilo načíst',
                        subtitle: browser.getLoadErrorSubtitle(),
                    }),
                ];
            }

            if ((browser.folderSubfolders || []).length === 0 && (browser.folderFiles || []).length === 0) {
                return [
                    renderStateRow({
                        title: 'Složka je prázdná',
                        subtitle: 'Ve vybrané složce nejsou žádné podsložky ani soubory.',
                    }),
                ];
            }

            return (browser.folderFiles || []).map((file) => {
                const userCanOpen = browser.canUserOpenFile(file);
                const userCanEdit = browser.canUserEditFile(file);
                const userCanDelete = browser.canUserDeleteFile(file);
                const fileNameTitle = userCanOpen
                    ? null
                    : 'Nemáš oprávnění otevřít tento soubor.';

                return ct.trow([
                    ct.tcell([
                        (
                            userCanOpen
                                ? ct.span('.pointer-cursor', { onclick: () => { browser.openFile(file.uid); } }, [
                                    ct.icon(file.icon || file.fileTypeIcon || 'file'),
                                    ' ',
                                    file.name,
                                ])
                                : ct.span('.italic.pointer-not-allowed', { title: fileNameTitle }, [
                                    ct.icon(file.icon || file.fileTypeIcon || 'file'),
                                    ' ',
                                    file.name,
                                ])
                        ),
                    ]),
                    ct.tcell('.min', ['Soubor']),
                    ct.tcell('.min.right-align', [
                        dtv.formatBytes(file.size) || '-',
                    ]),
                    ct.tcell('.min.right-align', [
                        dtv.formatDatetime(file.changedAt) || '-',
                    ]),
                    ct.tcell('.min.right-align', [
                        (
                            userCanEdit
                                ? ct.iconButton('edit', '.transparent.circle', {
                                    title: 'Upravit soubor',
                                    onclick: () => { browser.editFile(file.uid); }
                                })
                                : null
                        ),
                        (
                            userCanDelete
                                ? ct.iconButton('delete', '.transparent.circle', {
                                    title: 'Odstranit soubor',
                                    onclick: () => { browser.deleteFile(file.uid); }
                                })
                                : null
                        ),
                    ]),
                ]);
            });

        };

        function renderStateRow({ title, subtitle }) {

            return ct.trow([
                ct.tcell({ colspan: 5 }, [
                    ct.empty({
                        title,
                        subtitle,
                        icon: ct.icon('folder-open', '.fa-3x'),
                    }),
                ]),
            ]);

        }

    }

}
